import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { AddRolloutDraftsResponse } from '../../../../../redux/api/fymAPI.types';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { Panel } from '../../../components/primitives/Panel';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { aiReelStudioParams } from '../../studioRoute';
import { StudioActionFeedback } from '../../studio/studioActionErrorText';
import { AddDraftsResult } from './AddDraftsResult';
import { planMessages } from './planMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Plan tab; each part uses its own slice of it */
import styles from './Plan.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface GapBarProps {
  catalogItemUuid: string;
  /** "2 days have no post". */
  title: string;
  /** The days and what the click makes first. */
  lines: string[];
  /** A day needs an AI reel that is not made: the bar offers "Make an AI reel" (the Studio). */
  needsReel: boolean;
  buttonLabel: string;
  /** False with no channel, no day to add, or while the credits are short. */
  canAdd: boolean;
  isAdding: boolean;
  onAdd: () => void;
  result: AddRolloutDraftsResponse | null;
  error: StudioActionFeedback | null;
}

/**
 * The gap bar under the timeline (designs f1, f2): how many days have no post, which assets the
 * click makes first, "Add N drafts", and the result of the click next to the button.
 */
const GapBarComponent: React.FC<GapBarProps> = ({
  catalogItemUuid,
  title,
  lines,
  needsReel,
  buttonLabel,
  canAdd,
  isAdding,
  onAdd,
  result,
  error,
}) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const openReel = useCallback(
    () => router.push(SMLRoutes.promoteStudio, aiReelStudioParams({ catalogItemUuid })),
    [catalogItemUuid, router]
  );

  return (
    <Panel nested className={styles.gapBar} data-testid="plan-gap-bar">
      <div className={styles.gapBarText}>
        <b>{title}</b>
        {lines.map((line) => (
          <span key={line}>{line}</span>
        ))}
        {needsReel && <span>{intl.formatMessage(planMessages.gapReel)}</span>}
      </div>
      <div className={styles.gapBarActions}>
        {needsReel && <SMLButton onClick={openReel}>{intl.formatMessage(planMessages.openStudioReel)}</SMLButton>}
        <SMLButton variant="primary" onClick={onAdd} disabled={!canAdd || isAdding}>
          {buttonLabel}
        </SMLButton>
      </div>
      <AddDraftsResult result={result} error={error} />
    </Panel>
  );
};

export const GapBar = TypedMemo(GapBarComponent);
